/**
 * THE user lifecycle transition table — one declaration, read by the API and
 * by the browser.
 *
 * Foundations 2 (Users rail), B2a-T2. principles.md #1 ("one store, one
 * resolver, one owner per noun"), #3 ("nothing that describes a noun is
 * hardcoded") and #4 ("nothing fails open").
 *
 * ─── What this file is ────────────────────────────────────────────────────
 *
 * The ALPHABET of user lifecycle states, and the GRAPH of transitions between
 * them: for every edge, what it is called, which permission key it costs, who
 * performs it, which endpoint performs it, whether it needs a reason, whether
 * it needs a confirmation, and what the operator should be warned about.
 *
 * ─── Why it moved here ────────────────────────────────────────────────────
 *
 * It lived at `packages/api/src/services/user-lifecycle.service.ts:75` as
 * `TRANSITION_TABLE`, a five-row map of `from` to a bare array of `to` values,
 * and NOTHING outside that module could read it. So the browser grew a second
 * copy of the same knowledge, written as control flow rather than data:
 * `packages/web/src/routes/users/$userId.tsx` derived `isLocked` /
 * `isSuspended` / `isDeactivated` / `isInvited` flags and hand-wired which
 * button each state should show, which titles to give them, which ones need a
 * reason, and which ones open a confirmation. Two declarations of one noun, in
 * two languages, kept in step by hand — the exact shape the one rule forbids.
 *
 * They had already drifted. The service's table had no `deleted` row, yet
 * `packages/api/src/routes/v1/user-offboarding.ts:213` (deleted in B2c-T1) wrote
 * `status = 'deleted'` (claim C12); it had no revoked-invitation state, yet
 * the invitation revoke path produced a placeholder user that was neither
 * invited nor active; and its `invited` row was empty, which the browser
 * re-expressed as a hand-maintained `invitedTooltip` beside four separate
 * `disabled={… || isInvited}` props (issue #75).
 *
 * ONE declaration now. The API service imports it and declares nothing of its
 * own; `GET /users/:id/lifecycle` computes the caller's `allowed` / `refused`
 * lists from it and the browser RENDERS that answer, deriving nothing.
 *
 * ─── Reading the table ────────────────────────────────────────────────────
 *
 * `LIFECYCLE_TRANSITIONS[from]` is every edge OUT of `from`, whoever performs
 * it and whichever endpoint performs it. Two fields say which:
 *
 *   `surface`  WHICH endpoint moves the row. An endpoint filters the table to
 *              its own surface and refuses everything else, so adding an edge
 *              here never silently widens what an endpoint accepts.
 *   `actor`    WHO performs it. `'subject'` edges (accepting an invitation)
 *              are never offered to an administrator, however many permissions
 *              they hold — the invitee completes them with their token.
 *
 * ─── The permission on an edge is the permission the endpoint ENFORCES ─────
 *
 * Not a new gate and not an aspiration: each `permission` below is the key the
 * route checks, so a caller reading `GET /users/:id/lifecycle` is told exactly
 * what the write path will decide. Anything touching `locked` costs
 * `lifecycle:lock:users`; the suspend pair AND THE DEACTIVATE PAIR cost
 * `write:users`. Every invitation edge costs `write:users`, which is what
 * `POST /users/invitations/*` requires; every edge into `deleted` costs
 * `delete:users`, which is what `DELETE /users/:id` requires.
 *
 * ─── Deactivation is priced once, here (decisions.md 12, B2a-CO-7) ─────────
 *
 * The table used to price `→ deactivated` at `delete:users` while
 * `POST /users/bulk` and `POST /users/:id/toggle-status` (deleted since, by
 * USR-F1-T3) performed the same account disablement under `write:users`: two
 * answers to "what does it cost to switch someone off". The founder's call (2026-09-14) is that the PRODUCT
 * was right — deactivation is the safe, reversible alternative to deleting, so
 * it must not cost more than an edit — and that the TABLE is what changes.
 * Every path that deactivates (the status endpoint and the bulk action) now
 * asks this table for the price, so there is one.
 *
 * `deactivated → active` (Restore) moves with it (B2c-T1). The (since deleted)
 * toggle and the bulk `activate` action have always reversed a deactivation under
 * `write:users`; leaving the status endpoint's Restore at `delete:users` would
 * have been the same two-prices defect in the other direction, and a
 * reversible action whose reversal costs more than the action is the shape
 * decision 12 rejects.
 *
 * ─── Where the state is stored (B2c-T1, carry-over B2a-CO-6) ───────────────
 *
 * The alphabet below is the lifecycle of a person IN ONE TENANT and it lives
 * on the membership row, `user_organizations.status` (migration c31). It used
 * to live on `users.status`, a GLOBAL column, while every write to it was
 * tenant-scoped — so an administrator of tenant A who locked somebody locked
 * them out of tenant B as well, because the liveness gate read the one global
 * answer on every request. The global row now carries only whether the
 * ACCOUNT exists (`ACCOUNT_STATES`, below); a lock, a suspension or a
 * deactivation issued in one tenant cannot reach another.
 *
 * ─── Fail closed ──────────────────────────────────────────────────────────
 *
 * An unknown `from` has NO transitions, not every transition: `allowedTransitions`
 * returns a frozen empty array rather than `undefined` or a permissive default,
 * and `findTransition` answers `null`. A state with no outgoing edges —
 * `deleted` — is terminal by the data, not by a special case in a caller.
 */

import { z } from 'zod';
import { describePermission, type RbacPermission } from './permissions.js';

// ─── The state alphabet ──────────────────────────────────────────────────────

/**
 * Every value `user_organizations.status` may hold — a person's lifecycle IN
 * ONE TENANT — as ONE list. c31's CHECK constraint is this list.
 *
 *   `deleted`          removed from the tenant by `deleteUser`
 *                      (`api/src/services/user-lifecycle.service.ts`), the one
 *                      writer of it since B2c-T1 deleted the second lifecycle
 *                      path (`routes/v1/user-offboarding.ts`, claim C12).
 *   `invited_revoked`  the state a placeholder user is left in when their
 *                      invitation is revoked (B2c-T2).
 *
 * `inactive` is NOT here. It is a legacy v0 value with no distinct meaning;
 * `normalizeLifecycleState` folds it into `active`, and c31 normalised the
 * stored rows and now refuses it at the CHECK.
 */
export const LifecycleStateSchema = z.enum([
  'invited',
  'invited_revoked',
  'active',
  'locked',
  'suspended',
  'deactivated',
  'deleted',
]);
export type LifecycleState = z.infer<typeof LifecycleStateSchema>;

/** The same list as a plain array, in declaration order. */
export const LIFECYCLE_STATES: readonly LifecycleState[] = Object.freeze([
  ...LifecycleStateSchema.options,
]);

/**
 * WHICH MEMBERSHIP STATES MAY MAKE A REQUEST — the tenant half of "is this
 * person live?", declared ONCE (Foundations 2, B2a-T3; moved onto the
 * membership by B2c-T1).
 *
 * `policy/principal.ts` `liveMembershipQuery` reads this list and nothing
 * else: the liveness predicate binds these values against
 * `user_organizations.status` instead of spelling a status vocabulary into
 * SQL, so a state added to the alphabet above can never quietly become a
 * state that authenticates.
 *
 * ONE state is on it. Every other state in the alphabet names an account an
 * administrator has stopped, or one that has not started:
 *
 *   `invited`          no password yet; `POST /auth/accept-invite` sets
 *                      `status = 'active'` and `is_active = true` in the same
 *                      UPDATE, so acceptance makes them live in one step and
 *                      nobody waits on a second write.
 *   `invited_revoked`  un-invited (B2c-T2). Never activated.
 *   `locked`           admin-locked.
 *   `suspended`        admin-suspended — already refused at `/auth/login`.
 *   `deactivated`      offboarded, inside the restore window.
 *   `deleted`          removed from the tenant (B2c-T1).
 *
 * THE OPPOSITE DIRECTION FROM `normalizeLifecycleState`, deliberately. That
 * function folds an unrecognised value to `active` because it answers "what
 * may an administrator do TO this row" — the SOURCE side, where guessing wide
 * offers an action that the write path re-checks. This list answers "may this
 * person ACT", where guessing wide is an authentication bypass. So it is a
 * positive list: a value that is not on it does not authenticate, whatever it
 * is (principles.md #4).
 *
 * The legacy v0 value `inactive` is therefore NOT live, and costs nothing:
 * every writer of it sets `is_active = false` in the same statement
 * (`routes/v1/users.ts` bulk deactivate and bulk delete), which
 * the liveness predicate already refuses.
 */
export const LIFECYCLE_LIVE_STATES: readonly LifecycleState[] = Object.freeze([
  'active',
]);

/**
 * Is a RAW `user_organizations.status` value one that may make a request?
 *
 * `null` and `undefined` are NOT live (B2c-T1). Before c31 the lifecycle sat on
 * a nullable `users.status` and a NULL was read as a pre-W1a-13 row; c31 put
 * the state on the membership as `NOT NULL DEFAULT 'active'` and made
 * `users.status` NOT NULL too, so no stored row can be NULL and an absent
 * value can only mean a caller that did not read the column — which is an
 * unknown, and an unknown denies (principles.md #4). The SQL predicate in
 * `policy/principal.ts` mirrors this function exactly.
 */
export function isLiveLifecycleStatus(raw: string | null | undefined): boolean {
  if (raw === null || raw === undefined) return false;
  return (LIFECYCLE_LIVE_STATES as readonly string[]).includes(raw);
}

// ─── The ACCOUNT alphabet — the only lifecycle fact that is global ───────────

/**
 * Every value `users.status` may hold since c31 (B2c-T1, carry-over B2a-CO-6).
 *
 * The global row answers ONE question: does this account still exist? Every
 * other lifecycle state is a fact about a person IN A TENANT and lives on the
 * membership (`LifecycleStateSchema`). An account becomes `deleted` only when
 * `deleteUser` removes its LAST membership that is not already deleted, so a
 * removal issued in one tenant never ends a person's access to another one.
 */
export const AccountStateSchema = z.enum(['active', 'deleted']);
export type AccountState = z.infer<typeof AccountStateSchema>;

/** The account states that may make a request — a positive list. */
export const ACCOUNT_LIVE_STATES: readonly AccountState[] = Object.freeze([
  'active',
]);

/**
 * Is a RAW `users.status` value one that may make a request? A positive list
 * for the same reason `isLiveLifecycleStatus` is one: an unrecognised or
 * absent value does not authenticate.
 */
export function isLiveAccountStatus(raw: string | null | undefined): boolean {
  if (raw === null || raw === undefined) return false;
  return (ACCOUNT_LIVE_STATES as readonly string[]).includes(raw);
}

/**
 * How each state reads to a PERSON.
 *
 * The state ids above are machine vocabulary — `invited_revoked` has an
 * underscore in it — and `explainRefusal` writes sentences an operator reads
 * verbatim in a tooltip or a toast. So the display name lives on the alphabet,
 * once, beside the ids it names (review B2aT2-R4). This is the same rule the
 * edges already follow with `label`: nothing the browser shows a person is a
 * key.
 */
export const LIFECYCLE_STATE_LABELS: Readonly<Record<LifecycleState, string>> =
  Object.freeze({
    invited: 'invited',
    invited_revoked: 'revoked-invitation',
    active: 'active',
    locked: 'locked',
    suspended: 'suspended',
    deactivated: 'deactivated',
    deleted: 'deleted',
  });

/**
 * WHICH endpoint performs a transition.
 *
 *   `status`      PATCH /users/:id/status — the lifecycle state machine.
 *   `invitation`  the invitation flow: accept, revoke, re-invite.
 *   `delete`      DELETE /users/:id — removal from the tenant.
 *
 * An endpoint reads only its own surface. That is what stops a new edge added
 * for one surface from widening what another one accepts.
 */
export const LifecycleTransitionSurfaceSchema = z.enum([
  'status',
  'invitation',
  'delete',
]);
export type LifecycleTransitionSurface = z.infer<
  typeof LifecycleTransitionSurfaceSchema
>;

/**
 * WHO performs a transition.
 *
 *   `administrator`  somebody acting on another person's account, holding the
 *                    edge's permission.
 *   `subject`        the person themselves, through a token rather than a
 *                    permission — accepting an invitation is the only one.
 *
 * A `subject` edge is never `allowed` for an administrator no matter what they
 * hold, which is why the invited row of the old table looked empty: it was not
 * empty, it was somebody else's to walk.
 */
export const LifecycleTransitionActorSchema = z.enum(['administrator', 'subject']);
export type LifecycleTransitionActor = z.infer<
  typeof LifecycleTransitionActorSchema
>;

/**
 * HOW AN ACTION LOOKS, as a KEY rather than as a component or a class name
 * (principles.md #5, "reference by key, never by name, slug or path prefix").
 *
 * The Users screens render one control per transition. Which glyph and which
 * emphasis that control gets is part of what the transition IS — "Deactivate"
 * has been a red button with a struck-through person since W1a-02 — so it is
 * declared on the edge with everything else rather than re-decided by a
 * `to`-keyed lookup in the browser. A `to`-keyed lookup could not express it
 * anyway: `locked → active` is "Unlock", `suspended → active` is "Restore" and
 * `deactivated → active` is "Restore from soft-delete", three different glyphs
 * for one destination.
 *
 * The browser binds a key to a component. That binding is presentation and
 * nothing else: it carries no rule, decides no gate, and a key it does not
 * recognise still renders the control, with a neutral glyph.
 */
export const LifecycleTransitionIconSchema = z.enum([
  'check',
  'mail',
  'mail-x',
  'lock',
  'unlock',
  'pause',
  'play',
  'user-x',
  'rotate',
  'trash',
]);
export type LifecycleTransitionIcon = z.infer<
  typeof LifecycleTransitionIconSchema
>;

/**
 * HOW HARD AN ACTION BITES.
 *
 *   `normal`       reversible and cheap — lock, unlock, restore.
 *   `caution`      ends sessions or access, recoverable by an administrator.
 *   `destructive`  removes or soft-deletes the account.
 *
 * It drives emphasis, not authority: the permission on the edge decides who
 * may do it, and `requiresConfirm` decides whether they are asked twice.
 */
export const LifecycleTransitionSeveritySchema = z.enum([
  'normal',
  'caution',
  'destructive',
]);
export type LifecycleTransitionSeverity = z.infer<
  typeof LifecycleTransitionSeveritySchema
>;

/**
 * WHAT A TRANSITION DOES TO THE SOFT-DELETE WINDOW — the MEMBERSHIP's
 * `user_organizations.deleted_at` since c31 (B2c-T1), for the same reason the
 * state moved: a recovery window opened in one tenant is not a fact about the
 * person's other tenants.
 *
 *   `set`    stamp it at now + the recovery window, so the membership stays
 *            readable and restorable until the window closes.
 *   `purge`  stamp it at NOW — immediately purge-eligible, gone from every
 *            read, not restorable.
 *   `clear`  drop the stamp — the membership is live again.
 *   `none`   leave it alone.
 *
 * `purge` is what `deleteUser` (`api/src/services/user-lifecycle.service.ts`)
 * does for `DELETE /users/:id` and the bulk delete.
 * Before review B2aT2-BF-05 the four delete-surface edges said `set`, which
 * claimed a 60-day recovery window the delete endpoint has never given them —
 * the table's header promises these fields record what the endpoints do TODAY,
 * and for that surface they did not.
 */
export const LifecycleSoftDeleteEffectSchema = z.enum([
  'set',
  'purge',
  'clear',
  'none',
]);
export type LifecycleSoftDeleteEffect = z.infer<
  typeof LifecycleSoftDeleteEffectSchema
>;

/**
 * How long a deactivated account can be restored before the purge cron
 * (W1a-7) hard-deletes the row. Declared HERE because the transition warnings
 * quote it and `computeSoftDeleteAt` in the API service computes from it —
 * two readers, therefore one declaration.
 */
export const LIFECYCLE_SOFT_DELETE_WINDOW_DAYS = 60;

const RECOVERY_WINDOW = `${LIFECYCLE_SOFT_DELETE_WINDOW_DAYS}-day`;

const DELETE_WARNING =
  'Removes the person from this tenant along with their role assignments, ' +
  'group memberships and department memberships.';

/** One edge of the lifecycle graph. */
export interface LifecycleTransition {
  /** The destination state. */
  readonly to: LifecycleState;
  /** What the action is called wherever it is offered. */
  readonly label: string;
  /** The permission key the performing endpoint enforces. */
  readonly permission: RbacPermission;
  /** Does the write path require a free-text reason for the audit row? */
  readonly requiresReason: boolean;
  /** Should the operator be asked to confirm before it fires? */
  readonly requiresConfirm: boolean;
  /** Which endpoint moves the row. */
  readonly surface: LifecycleTransitionSurface;
  /** Who performs it. */
  readonly actor: LifecycleTransitionActor;
  /** The glyph key its control carries. */
  readonly icon: LifecycleTransitionIcon;
  /** How hard it bites, for emphasis. */
  readonly severity: LifecycleTransitionSeverity;
  /**
   * Does walking this edge kill the person's refresh tokens?
   *
   * THE SIDE-EFFECT MATRIX IS PART OF WHAT A TRANSITION IS, so it is declared
   * on the edge rather than re-derived by the service that applies it. It was
   * a second `if (to === 'locked' || to === 'suspended' || …)` chain inside
   * `applyLifecycleSideEffects`, which is the same knowledge in a second
   * place: adding a state to the table there left the chain silently stale.
   *
   * These DRIVE the endpoints. Since B2c-T1 every lifecycle write — the status
   * endpoint, `POST /users/bulk` and `DELETE /users/:id` through
   * `deleteUser` — applies its edge through
   * `applyLifecycleSideEffects` in `api/src/services/user-lifecycle.service.ts`,
   * which reads this flag and calls the ONE revocation (refresh tokens AND open
   * sockets). The second lifecycle path that used to revoke on its own,
   * `POST /users/:userId/offboard`, is deleted. For a row that never logged in
   * (an `invited` or `invited_revoked` subject) the revocation finds nothing to
   * end, which is the same answer, not a different rule.
   */
  readonly revokesSessions: boolean;
  /** What it does to `users.deleted_at`. */
  readonly softDelete: LifecycleSoftDeleteEffect;
  /** What the operator needs to know before they do it. */
  readonly warning?: string;
}

// ─── The table ───────────────────────────────────────────────────────────────

const TRANSITIONS = {
  invited: [
    {
      to: 'active',
      label: 'Activate',
      permission: 'write:users',
      requiresReason: false,
      requiresConfirm: false,
      surface: 'invitation',
      actor: 'subject',
      icon: 'check',
      severity: 'normal',
      revokesSessions: false,
      softDelete: 'none',
      warning:
        'The invitee completes this themselves by accepting their invitation; ' +
        'an administrator cannot activate the account for them.',
    },
    {
      to: 'invited_revoked',
      label: 'Revoke invitation',
      permission: 'write:users',
      requiresReason: false,
      requiresConfirm: true,
      surface: 'invitation',
      actor: 'administrator',
      icon: 'mail-x',
      severity: 'caution',
      revokesSessions: false,
      softDelete: 'none',
      warning:
        'The invitation link stops working immediately. The person keeps their ' +
        'place on the Users list and can be invited again.',
    },
    {
      to: 'deleted',
      label: 'Delete',
      permission: 'delete:users',
      requiresReason: false,
      requiresConfirm: true,
      surface: 'delete',
      actor: 'administrator',
      icon: 'trash',
      severity: 'destructive',
      revokesSessions: true,
      softDelete: 'purge',
      warning: DELETE_WARNING,
    },
  ],
  invited_revoked: [
    {
      to: 'invited',
      label: 'Re-invite',
      permission: 'write:users',
      requiresReason: false,
      requiresConfirm: false,
      surface: 'invitation',
      actor: 'administrator',
      icon: 'mail',
      severity: 'normal',
      revokesSessions: false,
      softDelete: 'none',
      warning: 'Issues a fresh invitation link to the same person.',
    },
    {
      to: 'deleted',
      label: 'Delete',
      permission: 'delete:users',
      requiresReason: false,
      requiresConfirm: true,
      surface: 'delete',
      actor: 'administrator',
      icon: 'trash',
      severity: 'destructive',
      revokesSessions: true,
      softDelete: 'purge',
      warning: DELETE_WARNING,
    },
  ],
  active: [
    {
      to: 'locked',
      label: 'Lock',
      permission: 'lifecycle:lock:users',
      requiresReason: false,
      requiresConfirm: false,
      surface: 'status',
      actor: 'administrator',
      icon: 'lock',
      severity: 'caution',
      revokesSessions: true,
      softDelete: 'none',
      warning:
        'Revokes every active session and blocks sign-in until the account is unlocked.',
    },
    {
      to: 'suspended',
      label: 'Suspend',
      permission: 'write:users',
      requiresReason: true,
      requiresConfirm: true,
      surface: 'status',
      actor: 'administrator',
      icon: 'pause',
      severity: 'caution',
      revokesSessions: true,
      softDelete: 'none',
      warning:
        'All active sessions end immediately and the user cannot sign in until restored.',
    },
    {
      to: 'deactivated',
      label: 'Deactivate',
      // decisions.md 12 — an edit, not a removal.
      permission: 'write:users',
      requiresReason: true,
      requiresConfirm: true,
      surface: 'status',
      actor: 'administrator',
      icon: 'user-x',
      severity: 'destructive',
      revokesSessions: true,
      softDelete: 'set',
      warning:
        `Sessions are revoked and the account enters a ${RECOVERY_WINDOW} recovery window; ` +
        'after that the row is purged.',
    },
    {
      to: 'deleted',
      label: 'Delete',
      permission: 'delete:users',
      requiresReason: false,
      requiresConfirm: true,
      surface: 'delete',
      actor: 'administrator',
      icon: 'trash',
      severity: 'destructive',
      revokesSessions: true,
      softDelete: 'purge',
      warning: DELETE_WARNING,
    },
  ],
  locked: [
    {
      to: 'active',
      label: 'Unlock',
      permission: 'lifecycle:lock:users',
      requiresReason: false,
      requiresConfirm: false,
      surface: 'status',
      actor: 'administrator',
      icon: 'unlock',
      severity: 'normal',
      revokesSessions: false,
      softDelete: 'none',
      warning: 'Restores sign-in access. The user must authenticate again.',
    },
    {
      to: 'deleted',
      label: 'Delete',
      permission: 'delete:users',
      requiresReason: false,
      requiresConfirm: true,
      surface: 'delete',
      actor: 'administrator',
      icon: 'trash',
      severity: 'destructive',
      revokesSessions: true,
      softDelete: 'purge',
      warning: DELETE_WARNING,
    },
  ],
  suspended: [
    {
      to: 'active',
      label: 'Restore',
      permission: 'write:users',
      requiresReason: false,
      requiresConfirm: true,
      surface: 'status',
      actor: 'administrator',
      icon: 'play',
      severity: 'normal',
      revokesSessions: false,
      softDelete: 'none',
      warning: 'The user regains sign-in access and must authenticate again.',
    },
    {
      to: 'deactivated',
      label: 'Deactivate',
      // decisions.md 12 — an edit, not a removal.
      permission: 'write:users',
      requiresReason: true,
      requiresConfirm: true,
      surface: 'status',
      actor: 'administrator',
      icon: 'user-x',
      severity: 'destructive',
      revokesSessions: true,
      softDelete: 'set',
      warning:
        `Sessions are revoked and the account enters a ${RECOVERY_WINDOW} recovery window; ` +
        'after that the row is purged.',
    },
    {
      to: 'deleted',
      label: 'Delete',
      permission: 'delete:users',
      requiresReason: false,
      requiresConfirm: true,
      surface: 'delete',
      actor: 'administrator',
      icon: 'trash',
      severity: 'destructive',
      revokesSessions: true,
      softDelete: 'purge',
      warning: DELETE_WARNING,
    },
  ],
  deactivated: [
    {
      to: 'active',
      label: 'Restore',
      // The reversal of decision 12's edit costs what the edit costs — see the
      // file header.
      permission: 'write:users',
      requiresReason: false,
      requiresConfirm: true,
      surface: 'status',
      actor: 'administrator',
      icon: 'rotate',
      severity: 'normal',
      revokesSessions: false,
      softDelete: 'clear',
      warning:
        'Brings the user back from soft-delete. Restoration only works inside the ' +
        `${RECOVERY_WINDOW} recovery window.`,
    },
    {
      to: 'deleted',
      label: 'Delete',
      permission: 'delete:users',
      requiresReason: false,
      requiresConfirm: true,
      surface: 'delete',
      actor: 'administrator',
      icon: 'trash',
      severity: 'destructive',
      revokesSessions: true,
      softDelete: 'purge',
      warning: DELETE_WARNING,
    },
  ],
  /**
   * TERMINAL, and terminal in the DATA rather than in a caller's `if`. Every
   * consumer that asks this table what a deleted person can become is told
   * "nothing", which is why no endpoint needs its own deleted-is-final branch.
   */
  deleted: [],
} as const satisfies Record<LifecycleState, readonly LifecycleTransition[]>;

/**
 * THE transition table. One declaration; the API service imports it, the
 * lifecycle endpoint projects it, and the browser renders that projection.
 */
export const LIFECYCLE_TRANSITIONS: Readonly<
  Record<LifecycleState, readonly LifecycleTransition[]>
> = TRANSITIONS;

// ─── Derivations (never second lists) ────────────────────────────────────────

type DeclaredTransition = (typeof TRANSITIONS)[LifecycleState][number];

/**
 * The destinations `PATCH /users/:id/status` accepts — DERIVED from the table
 * rather than declared beside it, so the body schema and the state machine
 * cannot name different sets.
 */
export type LifecycleStatusDestination = Extract<
  DeclaredTransition,
  { surface: 'status' }
>['to'];

const statusDestinations: readonly LifecycleStatusDestination[] = Object.freeze(
  LIFECYCLE_STATES.filter((state) =>
    LIFECYCLE_STATES.some((from) =>
      LIFECYCLE_TRANSITIONS[from].some(
        (transition) => transition.surface === 'status' && transition.to === state
      )
    )
  ) as LifecycleStatusDestination[]
);

/** The status-endpoint destinations, in state-alphabet order. */
export const LIFECYCLE_STATUS_DESTINATIONS = statusDestinations;

/** The status-endpoint destination enum, built from the same derivation. */
export const LifecycleStatusDestinationSchema = z.enum(
  statusDestinations as unknown as [
    LifecycleStatusDestination,
    ...LifecycleStatusDestination[],
  ]
);

/**
 * Does ANY status edge into this destination require a reason?
 *
 * The question a caller can ask knowing only `to` — which is all a request
 * body carries, and all a bulk action on the Users roster knows. It is
 * DERIVED, here, once: `PATCH /users/:id/status`'s body refine asks it before
 * it has read the row, and the roster asks it to decide whether a bulk action
 * prompts for a reason before it fires. Both used to answer it for themselves
 * — the API by walking the table, the roster by a hardcoded
 * `to === 'suspended' || to === 'deactivated'` (review B2aT2-R1) — which is
 * the same knowledge in three places and drifts the moment an edge's
 * `requiresReason` changes.
 *
 * It is a FAST refusal, never the authority: once `from` is known the write
 * path re-checks `requiresReason` on the resolved edge.
 */
export function statusDestinationRequiresReason(
  to: LifecycleState
): boolean {
  return LIFECYCLE_STATES.some((from) =>
    LIFECYCLE_TRANSITIONS[from].some(
      (transition) =>
        transition.surface === 'status' &&
        transition.to === to &&
        transition.requiresReason
    )
  );
}

/**
 * Every permission the edges of ONE surface cost, derived from the table.
 *
 * The question an endpoint can ask BEFORE it knows which edge it is being
 * asked for: "could this caller perform any transition on this surface at
 * all?" `PATCH /users/:id/status` asks it as its FLOOR (SEC-B2aR6-01) — the
 * minimum authority needed to reach the handler — and then still charges the
 * resolved edge's own permission once `from` is known. An edge added with a
 * new permission widens the floor by exactly that key, with nothing for
 * anybody to remember.
 *
 * De-duplicated, sorted and frozen, so a test can compare the set rather than
 * a list nobody maintains.
 */
const SURFACE_PERMISSIONS: Readonly<
  Record<LifecycleTransitionSurface, readonly RbacPermission[]>
> = Object.freeze(
  Object.fromEntries(
    LifecycleTransitionSurfaceSchema.options.map((surface) => [
      surface,
      Object.freeze(
        Array.from(
          new Set(
            LIFECYCLE_STATES.flatMap((from) =>
              LIFECYCLE_TRANSITIONS[from]
                .filter((transition) => transition.surface === surface)
                .map((transition) => transition.permission)
            )
          )
        ).sort()
      ),
    ])
  ) as Record<LifecycleTransitionSurface, readonly RbacPermission[]>
);

const NO_PERMISSIONS: readonly RbacPermission[] = Object.freeze([]);

/**
 * The permissions `surface`'s edges cost. A surface with no edges answers with
 * an empty list, which is the FAIL-CLOSED answer for a floor built out of it:
 * no key admits anybody, rather than an absent floor admitting everybody.
 */
export function transitionPermissionsForSurface(
  surface: LifecycleTransitionSurface
): readonly RbacPermission[] {
  return SURFACE_PERMISSIONS[surface] ?? NO_PERMISSIONS;
}

/**
 * The sentence a FLOOR refusal carries on the status surface.
 *
 * It lives beside the table with every other lifecycle sentence rather than
 * inside the route, for the same reason `explainRefusal` does: the words a
 * person reads are written once. It names no edge and no key on purpose — the
 * floor is asked before the row has been read, so no edge is known yet, and a
 * caller who may not touch the surface is told nothing about the account.
 */
export const LIFECYCLE_STATUS_FLOOR_REFUSAL =
  'You do not hold a permission that can change the status of an account.';

const NO_TRANSITIONS: readonly LifecycleTransition[] = Object.freeze([]);

// ─── Reading the table ───────────────────────────────────────────────────────

/**
 * Normalise a raw `users.status` value to a state in the alphabet.
 *
 * `inactive` is the legacy v0 value and folds to `active`, which is what the
 * API service has done since W1a-13; anything unrecognised folds the same way,
 * which is the SOURCE side of the question and therefore not a widening — it
 * decides what an administrator may do TO the row, never what the row's owner
 * may do with it.
 */
export function normalizeLifecycleState(
  raw: string | null | undefined
): LifecycleState {
  switch (raw) {
    case 'invited':
    case 'invited_revoked':
    case 'locked':
    case 'suspended':
    case 'deactivated':
    case 'deleted':
      return raw;
    case 'active':
    case 'inactive': // legacy v0 — treated as 'active' for FROM purposes
    case null:
    case undefined:
    default:
      return 'active';
  }
}

/**
 * Every edge out of `from`, whoever walks it. An unknown state has none —
 * a frozen empty array, never `undefined` and never a permissive default.
 */
export function allowedTransitions(
  from: LifecycleState
): readonly LifecycleTransition[] {
  return LIFECYCLE_TRANSITIONS[from] ?? NO_TRANSITIONS;
}

/** The declared edge `from → to`, or null when the graph has none. */
export function findTransition(
  from: LifecycleState,
  to: LifecycleState
): LifecycleTransition | null {
  return allowedTransitions(from).find((t) => t.to === to) ?? null;
}

/** Is `to` reachable from `from` through the given endpoint? */
export function isTransitionAllowed(
  from: LifecycleState,
  to: LifecycleState,
  surface: LifecycleTransitionSurface
): boolean {
  const transition = findTransition(from, to);
  return transition !== null && transition.surface === surface;
}

// ─── Refusals, in words, in one place ────────────────────────────────────────

/**
 * WHY a transition was refused. The endpoint names the cause; the prose for
 * every cause lives here, so the sentence a person reads is written once and
 * an endpoint never invents its own wording for a rule it did not author.
 */
export type LifecycleRefusalCause =
  | { readonly kind: 'not-declared' }
  | { readonly kind: 'terminal' }
  | { readonly kind: 'same-state' }
  | { readonly kind: 'wrong-surface'; readonly surface: LifecycleTransitionSurface }
  | { readonly kind: 'subject-only' }
  | { readonly kind: 'permission'; readonly permission: RbacPermission }
  | { readonly kind: 'self' }
  | { readonly kind: 'restore-window-expired' };

/** "a" or "an", so a state label can be dropped into a sentence. */
function indefiniteArticle(word: string): string {
  return /^[aeiou]/i.test(word) ? 'an' : 'a';
}

/** Sentence case for a phrase that starts one. */
function capitalise(phrase: string): string {
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

const SURFACE_PROSE: Readonly<Record<LifecycleTransitionSurface, string>> =
  Object.freeze({
    status: 'the lifecycle status endpoint',
    invitation: 'the invitation flow',
    delete: 'the delete-user endpoint',
  });

/**
 * The reason string a refused transition carries — the one the API returns on
 * a 409 and the one the browser renders in a disabled control's tooltip.
 *
 * With no `cause` it answers the graph question, which is what the old
 * `validateTransition` did: is there an edge at all, and is the source
 * terminal?
 */
export function explainRefusal(
  from: LifecycleState,
  to: LifecycleState,
  cause?: LifecycleRefusalCause
): string {
  const resolved: LifecycleRefusalCause =
    cause ??
    (from === to
      ? { kind: 'same-state' }
      : allowedTransitions(from).length === 0
        ? { kind: 'terminal' }
        : { kind: 'not-declared' });

  // Machine vocabulary never reaches the sentence (review B2aT2-R4). A state
  // is named by its `LIFECYCLE_STATE_LABELS` entry and an edge by its own
  // `label`; a permission by the catalogue's display name, from
  // `describePermission` — the one place a key is turned into words. A key the
  // catalogue cannot place has no display name, and rather than inventing one
  // (or leaking the raw key) the sentence names the ACTION the operator was
  // refused, which is the thing they were trying to do.
  const fromLabel = LIFECYCLE_STATE_LABELS[from] ?? from;
  const toLabel = LIFECYCLE_STATE_LABELS[to] ?? to;
  const fromAccount = `${indefiniteArticle(fromLabel)} ${fromLabel} account`;
  const actionLabel = findTransition(from, to)?.label;

  switch (resolved.kind) {
    case 'same-state':
      return `This account is already ${toLabel}.`;
    case 'terminal':
      return `${capitalise(fromAccount)} has no remaining lifecycle actions.`;
    case 'not-declared':
      return `${capitalise(fromAccount)} cannot be made ${toLabel}.`;
    case 'wrong-surface':
      return `Making ${fromAccount} ${toLabel} is done through ${SURFACE_PROSE[resolved.surface]}, not this one.`;
    case 'subject-only':
      return `Only the person themselves can do this — an administrator cannot make ${fromAccount} ${toLabel} for them.`;
    case 'permission': {
      const described = describePermission(resolved.permission);
      if (described) {
        return `You do not hold the “${described.label}” permission, which this action requires.`;
      }
      return actionLabel
        ? `You do not hold the permission “${actionLabel}” requires.`
        : 'You do not hold the permission this action requires.';
    }
    case 'self':
      return 'You cannot change your own lifecycle status.';
    case 'restore-window-expired':
      return `The ${RECOVERY_WINDOW} recovery window has passed, so this account can no longer be restored.`;
    default: {
      // Exhaustiveness: a new cause without prose is a compile error here
      // rather than an empty tooltip in front of an operator.
      const unreachable: never = resolved;
      void unreachable;
      return `${capitalise(fromAccount)} cannot be made ${toLabel}.`;
    }
  }
}

// ─── THE DECISION, in ONE place, in ONE order ────────────────────────────────

/**
 * WHAT A CALLER KNOWS about one request, that the table cannot know for
 * itself. Everything else — whether the edge exists, whose it is, which
 * endpoint walks it, what it costs — is on the edge.
 *
 *   `surface`            the endpoint the caller is asking ABOUT. The write
 *                        passes the endpoint it IS (`'status'`); the read,
 *                        which projects every action the account has, passes
 *                        each edge's own surface, because it is answering
 *                        "could this be done, through the endpoint that owns
 *                        it" rather than "may I do it here".
 *   `isSelf`             is the caller the subject of the row?
 *   `holdsPermission`    did `can()` grant the EDGE's `permission`? The table
 *                        never asks the RBAC resolver itself — one resolver,
 *                        one owner — it is told the answer.
 *   `restoreWindowOpen`  is `users.deleted_at` still in the future?
 */
export interface LifecycleDecisionFacts {
  readonly surface: LifecycleTransitionSurface;
  readonly isSelf: boolean;
  readonly holdsPermission: boolean;
  readonly restoreWindowOpen: boolean;
}

/** Either the edge, walkable, or the ONE refusal that applies to it. */
export type LifecycleDecision =
  | { readonly outcome: 'allowed'; readonly transition: LifecycleTransition }
  | {
      readonly outcome: 'refused';
      readonly cause: LifecycleRefusalCause;
      readonly reason: string;
    };

/**
 * THE ORDER THE QUESTIONS ARE ASKED IN — a contract, not an accident of two
 * if-statements in two files.
 *
 * It is exported so a test can assert the order rather than re-read it, and
 * documented here because the order DECIDES which sentence a person sees when
 * more than one refusal is true at once. Every element is a
 * `LifecycleRefusalCause['kind']`, most-selective first:
 *
 *   1 `same-state`               nothing is being asked for.
 *   2 `not-declared` / `terminal`  the graph has no such edge.
 *   3 `subject-only`             the edge is the subject's own to walk, and no
 *                                administrator can walk it through ANY
 *                                endpoint. It is asked BEFORE the surface on
 *                                purpose (PROVE-B2a-R2-C1): `invited → active`
 *                                is declared `actor: 'subject'` AND
 *                                `surface: 'invitation'`, and answering the
 *                                surface first sends an administrator to the
 *                                invitation flow, where they still cannot do
 *                                it. The true answer is that it is not theirs.
 *   4 `wrong-surface`            a real edge, walked by another endpoint.
 *   5 `self`                     separation of duties on the status and
 *                                delete surfaces.
 *   6 `permission`               the edge's key, which `can()` refused.
 *   7 `restore-window-expired`   the row is past its purge stamp.
 *
 * A caller who may not act is never told what the action would have required:
 * `permission` sits above `restore-window-expired` for that reason, and the
 * reason requirement is not here at all — it is a malformed request for an
 * action the caller MAY take, which the write asks after this function.
 */
export const LIFECYCLE_REFUSAL_PRECEDENCE: readonly LifecycleRefusalCause['kind'][] =
  Object.freeze([
    'same-state',
    'not-declared',
    'terminal',
    'subject-only',
    'wrong-surface',
    'self',
    'permission',
    'restore-window-expired',
  ]);

/**
 * MAY THIS TRANSITION BE WALKED, BY THIS CALLER, RIGHT NOW — and if not, which
 * ONE refusal applies.
 *
 * THIS IS THE WHOLE POINT OF THE FILE (PROVE-B2a-R2-C1). Before it, the read
 * (`GET /users/:id/lifecycle`) and the write (`PATCH /users/:id/status`) each
 * asked the table's questions in their OWN order — the read starting at
 * `actor`, the write starting at `surface` — and the previous round made them
 * share the SENTENCES without making them share the QUESTIONS. So the one edge
 * declared with two refusing properties, `invited → active`, was refused
 * "Only the person themselves can do this…" by the screen and "…is done
 * through the invitation flow, not this one." by the API, for one request.
 * Any edge declared later with two refusing properties inherited the same
 * split, which is why the fix is the function and not the row.
 *
 * The read PROJECTS what this returns; the write ACTS on what it returns.
 * Neither decides anything itself, and neither owns a sequence any more.
 */
export function decideTransition(
  from: LifecycleState,
  to: LifecycleState,
  facts: LifecycleDecisionFacts
): LifecycleDecision {
  const refuse = (cause: LifecycleRefusalCause): LifecycleDecision => ({
    outcome: 'refused',
    cause,
    reason: explainRefusal(from, to, cause),
  });

  // 1. Nothing is being asked for.
  if (from === to) return refuse({ kind: 'same-state' });

  // 2. Is there an edge at all? A source with no outgoing edges is terminal;
  //    anything else is simply not declared. This is the branch `explainRefusal`
  //    picks for itself when it is given no cause, asked explicitly here so the
  //    cause is on the answer rather than hidden inside the sentence.
  const transition = findTransition(from, to);
  if (transition === null) {
    return refuse(
      allowedTransitions(from).length === 0
        ? { kind: 'terminal' }
        : { kind: 'not-declared' }
    );
  }

  // 3. Whose edge is it? See the precedence note above for why this outranks
  //    the surface.
  if (transition.actor !== 'administrator') {
    return refuse({ kind: 'subject-only' });
  }

  // 4. Which endpoint walks it?
  if (transition.surface !== facts.surface) {
    return refuse({ kind: 'wrong-surface', surface: transition.surface });
  }

  // 5. Separation of duties, where the platform declares it: the lifecycle
  //    status endpoint AND the delete endpoint. Written against the EDGE's
  //    surface rather than the caller's, though step 4 has just made them
  //    equal, because the rule is a property of the state machine and not of
  //    who asked.
  //
  //    THE DELETE SURFACE JOINED IN B2c-T1. The second lifecycle path this
  //    batch deleted (`POST /users/:id/offboard`) refused to offboard its own
  //    caller; `DELETE /users/:id` never did, so an administrator could remove
  //    themselves from the tenant — and, if they were its last Owner, leave it
  //    ownerless. One path keeps the stricter of the two rules, and keeps it
  //    HERE, so the lifecycle read shows the Delete control refused with the
  //    same sentence the write answers.
  if (
    facts.isSelf &&
    (transition.surface === 'status' || transition.surface === 'delete')
  ) {
    return refuse({ kind: 'self' });
  }

  // 6. The edge's own key, as `can()` answered it.
  if (!facts.holdsPermission) {
    return refuse({ kind: 'permission', permission: transition.permission });
  }

  // 7. The recovery window, for the edges that reopen it — `softDelete:
  //    'clear'`, declared on the edge, never a second (from, to) comparison.
  if (transition.softDelete === 'clear' && !facts.restoreWindowOpen) {
    return refuse({ kind: 'restore-window-expired' });
  }

  return { outcome: 'allowed', transition };
}

// ─── The wire shape of GET /users/:id/lifecycle ──────────────────────────────

/**
 * One action the caller may take right now. Everything a control needs to
 * render itself is here, because the browser derives nothing: the words on it,
 * whether it prompts, whether it needs a reason, what to warn about, and which
 * endpoint it calls.
 */
export const UserLifecycleAllowedTransitionSchema = z.object({
  to: LifecycleStateSchema,
  label: z.string(),
  requiresReason: z.boolean(),
  requiresConfirm: z.boolean(),
  surface: LifecycleTransitionSurfaceSchema,
  icon: LifecycleTransitionIconSchema,
  severity: LifecycleTransitionSeveritySchema,
  warning: z.string().optional(),
});
export type UserLifecycleAllowedTransition = z.infer<
  typeof UserLifecycleAllowedTransitionSchema
>;

/**
 * One action declared for this state that the caller may NOT take, and the
 * sentence explaining why. A refused entry is rendered, disabled, with its
 * reason — an operator who cannot see the action cannot find out why.
 */
export const UserLifecycleRefusedTransitionSchema = z.object({
  to: LifecycleStateSchema,
  label: z.string(),
  surface: LifecycleTransitionSurfaceSchema,
  icon: LifecycleTransitionIconSchema,
  severity: LifecycleTransitionSeveritySchema,
  reason: z.string(),
});
export type UserLifecycleRefusedTransition = z.infer<
  typeof UserLifecycleRefusedTransitionSchema
>;

/** The whole answer to "what can be done with this account, by me, now?" */
export const UserLifecycleResponseSchema = z.object({
  state: LifecycleStateSchema,
  allowed: z.array(UserLifecycleAllowedTransitionSchema),
  refused: z.array(UserLifecycleRefusedTransitionSchema),
});
export type UserLifecycleResponse = z.infer<typeof UserLifecycleResponseSchema>;

/**
 * The error code `PATCH /users/:id/status` returns on a refused transition.
 *
 * It used to be a bare 400 carrying `invalid transition: x → y`, which reads
 * as "you sent nonsense" for a request that was well-formed and simply not
 * legal from the row's current state. 409 is the state conflict it is, and the
 * body carries the reason so the browser renders words rather than a code.
 */
export const LIFECYCLE_INVALID_TRANSITION_CODE = 'INVALID_TRANSITION' as const;
